/**
 * PullToRefresh Component - iOS-style pull-to-refresh for mobile
 *
 * Usage:
 * <PullToRefresh onRefresh={async () => { await fetchData(); }}>
 *   <YourContent />
 * </PullToRefresh>
 */

import React, { useState, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

// Haptic feedback helper
const triggerHaptic = async (style = 'medium') => {
  try {
    if (window.Capacitor) {
      const impactStyle = style === 'light' ? ImpactStyle.Light :
                          style === 'heavy' ? ImpactStyle.Heavy : ImpactStyle.Medium;
      await Haptics.impact({ style: impactStyle });
    }
  } catch (e) {
    // Haptics not available
  }
};

const PullToRefresh = ({
  onRefresh,
  children,
  threshold = 80,
  disabled = false
}) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;

    // Only enable pull-to-refresh when scrolled to top
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    if (scrollTop > 5) return;

    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [disabled, isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || disabled || isRefreshing) return;

    currentY.current = e.touches[0].clientY;
    const distance = Math.max(0, currentY.current - startY.current);

    // Apply resistance - pull distance diminishes as you pull further
    const resistedDistance = Math.min(distance * 0.5, threshold * 1.5);

    // Haptic feedback when crossing threshold
    if (resistedDistance >= threshold && pullDistance < threshold) {
      triggerHaptic('medium');
    }

    setPullDistance(resistedDistance);
  }, [isPulling, disabled, isRefreshing, threshold, pullDistance]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled) return;

    setIsPulling(false);

    if (pullDistance >= threshold && onRefresh) {
      // Haptic feedback when refresh starts
      triggerHaptic('heavy');

      setIsRefreshing(true);
      setPullDistance(threshold * 0.6); // Keep indicator visible during refresh

      try {
        await onRefresh();
        // Haptic feedback when refresh completes
        triggerHaptic('light');
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [isPulling, disabled, pullDistance, threshold, onRefresh]);

  const progress = Math.min(pullDistance / threshold, 1);
  const rotation = progress * 360;

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative"
    >
      {/* Pull indicator */}
      {(pullDistance > 0 || isRefreshing) && (
        <div
          className="absolute left-0 right-0 flex justify-center items-center z-10 pointer-events-none"
          style={{
            top: -40,
            transform: `translateY(${pullDistance}px)`,
            opacity: Math.min(progress, 1)
          }}
        >
          <div className={`bg-white rounded-full p-2 shadow-lg border border-gray-200 ${isRefreshing ? 'animate-spin' : ''}`}>
            <RefreshCw
              className="w-5 h-5 text-blue-600"
              style={{
                transform: isRefreshing ? 'none' : `rotate(${rotation}deg)`,
                transition: isRefreshing ? 'none' : 'transform 0.1s ease-out'
              }}
            />
          </div>
        </div>
      )}

      {/* Content with pull transform */}
      <div
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance * 0.3}px)` : 'none',
          transition: isPulling ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
